import React, { useContext } from 'react';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import { Box, Divider } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import RestrictToRole from '@components/RestrictToRole';
import SectionHeader from '@components/SectionHeader';
import { canRedirectTo, CURRENT_APP_VERSION, OTHER_APP_VERSION } from '@core/constants/appVersion';
import useAppVersionPreference from '@core/hooks/useAppVersionPreference';
import useSnackbar from '@core/hooks/useSnackbar';
import { AuthContext } from '@core/providers/AuthProvider';
import { RoleName } from '@core/types';
import buildAppSwitchUrl from '@core/utils/buildAppSwitchUrl';
import isCustomErroHandlerResponse from '@core/utils/isCustomErrorHandlerResponse';

const AppVersionOptions = () => {
  const { preferredVersion, setPreferredVersion } = useAppVersionPreference();
  const showSnackbar = useSnackbar();
  const { organization } = useContext(AuthContext);

  const isUsingOtherVersion = preferredVersion === OTHER_APP_VERSION;

  // Hide entirely if this deployment was never given the other app's URL
  // (missing/unresolved pipeline variable) — there'd be nowhere to send
  // the user, so don't offer a switch that can't work.
  if (!canRedirectTo(OTHER_APP_VERSION)) {
    return null;
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const targetVersion = checked ? OTHER_APP_VERSION : CURRENT_APP_VERSION;
    const result = await setPreferredVersion(targetVersion);

    if (!isCustomErroHandlerResponse(result) && result?.success) {
      window.location.href = buildAppSwitchUrl(targetVersion, {
        organizationId: organization?.organizationId,
      });
      return;
    }

    showSnackbar({
      type: 'error',
      title: 'Error',
      message: 'No se pudo cambiar de versión. Intentá nuevamente.',
    });
  };

  return (
    <RestrictToRole allowedRoles={[RoleName.Radiologist]}>
      <SectionHeader title="Versión de la aplicación" IconComponent={SwapHorizOutlinedIcon} />
      <Divider />
      <Box
        sx={{
          marginTop: '2rem',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <FormControlLabel
          control={<Switch onChange={handleChange} checked={isUsingOtherVersion} />}
          label="Usar la versión nueva de Evodicom."
        />
      </Box>
    </RestrictToRole>
  );
};

export default AppVersionOptions;
